import React, { useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Switch, useTheme } from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { isNull } from '@cultwines/zellar-client-sdk/utils/isNull';
import makeStyles from '@mui/styles/makeStyles';
import { isUndefined } from '@cultwines/zellar-client-sdk/utils/isUndefined';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { formatter } from '../../utils/currencyFormatter';
import Typography from '../Typography';
import Divider from '../Divider';
import { TradeVariant } from '../OrderModal/types';
import { REWARD_SETTING, SET_REWARD_SETTING } from '../../graphql/queries/rewardSetting';
import { REWARD_BALANCE } from '../../graphql/queries/rewardBalance';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.25),
    textTransform: 'uppercase',
  },
  row: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
  },
  fontSize14px: {
    fontSize: '14px',
  },

  fontSize12px: {
    fontSize: '12px',
  },

  fontSize18px: {
    fontSize: '14px',
  },

  fontWeight400: {
    fontWeight: 400,
  },
  fontWeight700: {
    fontWeight: 700,
  },

  checkBoxRow: {
    display: 'flex',
    width: '100%',
    justifyContent: 'space-between',
    itemsAlign: 'center',
  },

  switchRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },

  totalRewardBalance: {
    marginTop: theme.spacing(1),
  },

  itemContainer: {
    border: `1px solid ${theme.palette.grey[100]}`,
    borderRadius: '4px',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

interface CostBreakdownProps {
  variant: TradeVariant;
  transactionFee: number;
  totalPricePlusFees: number;
  totalUnitPrice: number;
  unitPrice: number;
  vat?: number;
  className?: string;
  showFees?: boolean;
  availableRewardBalance?: number;
  onRewardsToggle?: () => void;
}

export default function CostBreakdown({
  totalPricePlusFees,
  transactionFee,
  totalUnitPrice,
  unitPrice,
  variant,
  vat,
  className = '',
  showFees = true,
  availableRewardBalance,
  onRewardsToggle,
}: CostBreakdownProps): JSX.Element {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const theme = useTheme();
  const { cultxRewards } = useFlags();

  const [isRewardEnabled, setIsRewardEnabled] = React.useState<null | undefined | boolean>(null);
  const [updateRewardSetting, { loading: updateRewardSettingLoading }] = useMutation(SET_REWARD_SETTING, {
    onError: () => null,
  });

  const { data: rewardBalanceData, loading: loadingRewardBalance } = useQuery(REWARD_BALANCE, {
    fetchPolicy: 'network-only',
  });
  const { data: rewardSettings, loading: isLoadingRewardPreference } = useQuery(REWARD_SETTING, {
    fetchPolicy: 'network-only',
  });

  const totalRewardBalance = useMemo(() => {
    if (loadingRewardBalance) return null;
    return rewardBalanceData?.rewardBalance?.availableCredit ?? 0;
  }, [rewardBalanceData?.rewardBalance?.availableCredit, loadingRewardBalance]);

  const stwichConfig = useMemo(() => {
    return {
      isDisabled: !totalRewardBalance,
      isChecked: totalRewardBalance ? Boolean(isRewardEnabled) : false,
    };
  }, [totalRewardBalance, isRewardEnabled]);

  React.useEffect(() => {
    if (isLoadingRewardPreference) return;
    setIsRewardEnabled(rewardSettings?.rewardSetting?.preference);
  }, [rewardSettings?.rewardSetting?.preference, isLoadingRewardPreference]);

  const updateRewardPreference = async (bool: boolean) => {
    const result = await updateRewardSetting({ variables: { preference: bool } });
    const status = result?.data?.rewardSetting?.status;

    if (status !== 'Succeed') {
      enqueueSnackbar('Failed to enable reward balance', { variant: 'error' });
      return;
    }

    if (bool) {
      enqueueSnackbar('Reward balance was successfuly enabled', {
        variant: 'success',
      });
    }

    onRewardsToggle?.();
    setIsRewardEnabled(bool);
  };

  return (
    <div className={clsx(className, classes.container)}>
      <Typography className={clsx(classes.fontSize14px, classes.fontWeight700)}>
        {t(`product:detailsModal.${variant}.costBreakdown.title` as const)}
      </Typography>
      <Divider />
      <div className={classes.row}>
        <Typography zellarColour="textLabel" className={clsx(classes.fontSize14px, classes.fontWeight700)}>
          {t(`product:detailsModal.${variant}.costBreakdown.unitPrice` as const)}
        </Typography>
        <Typography className={clsx(classes.fontSize14px, classes.fontWeight700)}>
          {formatter.format(unitPrice)}
        </Typography>
      </div>
      <div className={classes.row}>
        <Typography zellarColour="textLabel" className={clsx(classes.fontSize14px, classes.fontWeight700)}>
          {t(`product:detailsModal.${variant}.costBreakdown.totalUnitPrice` as const)}
        </Typography>
        <Typography className={clsx(classes.fontSize14px, classes.fontWeight700)}>
          {formatter.format(totalUnitPrice)}
        </Typography>
      </div>
      {showFees && (
        <>
          <div className={classes.row}>
            <Typography zellarColour="textLabel" className={clsx(classes.fontSize14px, classes.fontWeight700)}>
              {t('product:detailsModal.buyNow.transactionFee', { fee: 2.5 })}
            </Typography>
            <Typography className={clsx(classes.fontSize14px, classes.fontWeight700)}>
              {formatter.format(transactionFee)}
            </Typography>
          </div>
          {!isUndefined(vat) && (
            <div className={classes.row}>
              <Typography zellarColour="textLabel" className={clsx(classes.fontSize14px, classes.fontWeight700)}>
                {t('common:vat')} (20%)
              </Typography>
              <Typography className={clsx(classes.fontSize14px, classes.fontWeight700)}>
                {formatter.format(vat)}
              </Typography>
            </div>
          )}
        </>
      )}

      {!isUndefined(isRewardEnabled) && !isNull(isRewardEnabled) && cultxRewards && (
        <>
          <Divider />
          <Typography className={clsx(classes.fontSize14px, classes.fontWeight700)}>{t('common:rewards')}</Typography>
          <div className={classes.itemContainer}>
            <div className={classes.checkBoxRow}>
              <div>
                <Typography zellarColour="textLabel" className={clsx(classes.fontSize14px, classes.fontWeight400)}>
                  {t('common:availableCredit')}
                </Typography>
                <Typography
                  zellarColour="textLabel"
                  className={clsx(classes.fontSize14px, classes.fontWeight400, classes.totalRewardBalance)}
                >
                  {isNull(totalRewardBalance)
                    ? t('common:currentlyUnavailable')
                    : formatter.format(totalRewardBalance ?? 0)}{' '}
                  {t('common:balance')}
                </Typography>
              </div>
              <div className={classes.switchRow}>
                <Typography
                  className={clsx(classes.fontSize14px, classes.fontWeight400)}
                  style={{
                    color: isRewardEnabled ? 'black' : theme.palette.textLabel,
                  }}
                >
                  {isNull(availableRewardBalance)
                    ? t('common:currentlyUnavailable')
                    : formatter.format(availableRewardBalance ?? 0)}
                </Typography>
                <Switch
                  disabled={updateRewardSettingLoading || isLoadingRewardPreference || stwichConfig.isDisabled}
                  checked={stwichConfig.isChecked}
                  onChange={() => {
                    updateRewardPreference(Boolean(!isRewardEnabled));
                  }}
                  name="makeAsDefault"
                  color="primary"
                />
              </div>
            </div>
          </div>
        </>
      )}

      <Divider />
      <div className={classes.row}>
        <Typography className={clsx(classes.fontSize14px, classes.fontWeight700)}>
          {t(`product:detailsModal.${variant}.costBreakdown.totalCost` as const)}
        </Typography>
        <Typography className={clsx(classes.fontSize14px, classes.fontWeight700)} data-testid="total-cost">
          {formatter.format(totalPricePlusFees)}
        </Typography>
      </div>
      <Divider />
    </div>
  );
}
